import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import ModalGlobal from './ModalGlobal';
import CustomButton from './CustomButton';
import { post } from '../../Utility/Http';
import Utility from '../../Utility/Utility';
import { createDmcLead } from '../../V1/Modules/Home/Controller/HomeController';

const initialForm = {
    name: '',
    email: '',
    phone_number: '',
    message: '',
};

const initialErrors = {
    name: '',
    email: '',
    phone_number: '',
    message: '',
    list_property: '',
};

function ContactUsModal({ show, onHide, title = "Contact Us", showListPropertyCheckbox = false }) {
    const history = useHistory();
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState(initialErrors);
    const [loading, setLoading] = useState(false);
    const [listPropertyChecked, setListPropertyChecked] = useState(false);
    const [captchaCode, setCaptchaCode] = useState('');
    const [enteredCaptcha, setEnteredCaptcha] = useState('');
    const [captchaError, setCaptchaError] = useState(false);
    const canvasRef = useRef(null);
    const [partnerAgreementOpen, setPartnerAgreementOpen] = useState(false);
    const [partnerAgreementLoading, setPartnerAgreementLoading] = useState(true);
    const [partnerAgreementError, setPartnerAgreementError] = useState(false);
    // Generate random 5-character alphanumeric captcha
    const generateCaptcha = () => {
        const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
        let result = '';
        for (let i = 0; i < 5; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setCaptchaCode(result);
        setEnteredCaptcha('');
        setCaptchaError(false);
    };

    // Draw captcha with distorted text, background noise lines, and dots
    const drawCaptcha = (code) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Background
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#f4f6ec';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Random noise lines
        for (let i = 0; i < 4; i++) {
            ctx.strokeStyle = ['#c2caa6', '#8e966e', '#a3ab7f', '#cbd4ae'][i % 4];
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
            ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
            ctx.stroke();
        }

        // Random dots
        for (let i = 0; i < 30; i++) {
            ctx.fillStyle = '#b1b994';
            ctx.beginPath();
            ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, Math.PI * 2);
            ctx.fill();
        }

        // Characters drawing
        const colors = ['#474d24', '#2f3414', '#59602e', '#3c421b'];
        for (let i = 0; i < code.length; i++) {
            const char = code[i];
            ctx.save();
            ctx.font = 'bold 19px Poppins, Arial, sans-serif';
            ctx.fillStyle = colors[i % colors.length];

            const x = 12 + i * 16;
            const y = 24 + (Math.random() * 4 - 2);
            const angle = (Math.random() * 20 - 10) * (Math.PI / 180);

            ctx.translate(x, y);
            ctx.rotate(angle);
            ctx.fillText(char, 0, 0);
            ctx.restore();
        }
    };

    // Initialize or regenerate captcha whenever modal opens
    useEffect(() => {
        if (show) {
            generateCaptcha();
            setListPropertyChecked(false);
            setErrors(initialErrors);
        }
    }, [show]);

    // Redraw canvas whenever captcha code changes
    useEffect(() => {
        if (captchaCode) {
            drawCaptcha(captchaCode);
        }
    }, [captchaCode]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Block invalid characters while typing
        if (name === 'name') {
            // Allow only letters and spaces (no special characters or numbers)
            if (value && !/^[A-Za-z\s]*$/.test(value)) {
                return;
            }
        } else if (name === 'phone_number') {
            // Allow only numeric digits (no letters or special characters)
            if (value && !/^\d*$/.test(value)) {
                return;
            }
        }

        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = { ...initialErrors };
        let isValid = true;

        const trimmedName = form.name.trim();
        const trimmedPhone = form.phone_number.trim();
        const trimmedEmail = form.email.trim();
        const trimmedMessage = form.message.trim();

        // Name – required, only alphabets and spaces, 2 to 50 characters
        if (!trimmedName) {
            newErrors.name = 'Name is required.';
            isValid = false;
        } else if (trimmedName.length < 2) {
            newErrors.name = 'Name must be at least 2 characters.';
            isValid = false;
        } else if (!/^[A-Za-z\s]+$/.test(trimmedName)) {
            newErrors.name = 'Name should only contain letters and spaces.';
            isValid = false;
        }

        // Phone – required, exactly 10 digits valid Indian mobile number (or 10-15 digits standard)
        if (!trimmedPhone) {
            newErrors.phone_number = 'Phone number is required.';
            isValid = false;
        } else if (!/^[6-9]\d{9}$/.test(trimmedPhone) && !/^\d{10,15}$/.test(trimmedPhone)) {
            newErrors.phone_number = 'Enter a valid 10-digit phone number.';
            isValid = false;
        }

        // Email – optional, but if provided must follow strict standard email format
        if (trimmedEmail) {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(trimmedEmail)) {
                newErrors.email = 'Enter a valid email address (e.g. name@example.com).';
                isValid = false;
            }
        }

        // Message – required
        if (!trimmedMessage) {
            newErrors.message = 'Message is required.';
            isValid = false;
        } else if (trimmedMessage.length < 10) {
            newErrors.message = 'Message must be at least 10 characters.';
            isValid = false;
        }

        // Checkbox validation when coming from 'List your property'
        if (showListPropertyCheckbox && !listPropertyChecked) {
            newErrors.list_property = 'Please check the box to agree to the terms and continue.';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        // Captcha verification (strict case-sensitive check for uppercase and lowercase)
        if (!enteredCaptcha.trim()) {
            setCaptchaError(true);
            Utility.toastNotifications('Please enter the captcha.', 'Captcha Required', 'error');
            return;
        }

        if (enteredCaptcha.trim() !== captchaCode) {
            setCaptchaError(true);
            Utility.toastNotifications('Invalid captcha entered. Please match perfectly with given captcha.', 'Invalid Captcha', 'error');
            generateCaptcha();
            return;
        }

        // const contentJson = {};
        // if (showListPropertyCheckbox) {
        //     contentJson.list_property_message = form.message.trim();
        //     contentJson.contact_for = "List Property enquiry";
        // } else {
        //     contentJson.contact_us_message = form.message.trim();
        // }

        const contentJson = {};

        if (showListPropertyCheckbox) {
            // List your property
            contentJson.list_property_message = form.message.trim();
            contentJson.contact_for = "List Property Enquiry";

        } else if (title === "Contribution") {
            // Contribution
            contentJson.contact_us_message = form.message.trim();
            contentJson.contact_for = "Contribution Message";

        } else if (title === "MICE Corporate Tours") {
            // MICE Corporate Tours
            contentJson.contact_us_message = form.message.trim();
            contentJson.contact_for = "MICE Corporate Tours Enquiry";

        } else {
            // Default Contact Us
            contentJson.contact_us_message = form.message.trim();
            contentJson.contact_for = "Contact Us Enquiry";
        }


        const payload = [
            {
                customer_name: form.name.trim(),
                customer_phone: form.phone_number.trim(),
                customer_email: form.email.trim(),
                source_id: "Marketplace",
                lead_priority: "medium",
                remarks: "",
                stage_id: 1,
                created_by: 0,
                content_json: contentJson
            }
        ];
        setLoading(true);
        return createDmcLead(payload)
            .then((response) => {
                setLoading(false);
                if (response && response.length > 0 && response[0].success) {
                    Utility.toastNotifications('Your message has been sent successfully. We will get back to you shortly.', 'Success', 'success');
                    setForm(initialForm);
                    setErrors(initialErrors);
                    setEnteredCaptcha('');
                    setCaptchaError(false);
                    setListPropertyChecked(false);
                    onHide();
                }
            })
            .catch((error) => {
                setLoading(false);
                console.error("contact us error", error);
            });
    };

    const handleCancel = () => {
        setForm(initialForm);
        setErrors(initialErrors);
        setEnteredCaptcha('');
        setCaptchaError(false);
        setListPropertyChecked(false);
        onHide();
    };

    const handleTermsClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onHide();
        window.scrollTo(0, 0);
        history.push(`/${localStorage.getItem('i18nextLng') || 'en'}/terms-conditions`);
    };
    const handlePartnerAgreementClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setPartnerAgreementLoading(true);
        setPartnerAgreementError(false);
        setPartnerAgreementOpen(true);
    };

    const handlePartnerAgreementClose = () => {
        setPartnerAgreementOpen(false);
    };

    const formBody = (
        <div className="modalinnerbody contactUsModalBody">
            {title === "MICE Corporate Tours" && (
                <p className="mice-modal-subtitle">
                    Plan your corporate meetings, incentive trips,
                    conferences, and events with us.
                </p>
            )}
            {/* Name */}
            <div className="input_field_inner">
                <label className="input_label" htmlFor="cu_name">
                    Name <span className="required-star">*</span>
                </label>
                <input
                    id="cu_name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={`input__fields_property${errors.name ? ' field-error' : ''}`}
                    maxLength={50}
                    autoComplete="off"
                />
                {errors.name && <p className="error_div">{errors.name}</p>}
            </div>

            {/* Email */}
            <div className="input_field_inner">
                <label className="input_label" htmlFor="cu_email">
                    Email
                </label>
                <input
                    id="cu_email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    className={`input__fields_property${errors.email ? ' field-error' : ''}`}
                    maxLength={150}
                    autoComplete="off"
                />
                {errors.email && <p className="error_div">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div className="input_field_inner">
                <label className="input_label" htmlFor="cu_phone">
                    Phone Number <span className="required-star">*</span>
                </label>
                <input
                    id="cu_phone"
                    type="tel"
                    name="phone_number"
                    value={form.phone_number}
                    onChange={handleChange}
                    placeholder="Enter 10-digit phone number"
                    className={`input__fields_property${errors.phone_number ? ' field-error' : ''}`}
                    maxLength={10}
                    autoComplete="off"
                />
                {errors.phone_number && <p className="error_div">{errors.phone_number}</p>}
            </div>

            {/* Message */}
            <div className="input_field_inner">
                <label className="input_label" htmlFor="cu_message">
                    Message <span className="required-star">*</span>
                </label>
                <textarea
                    id="cu_message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Write your message here..."
                    className={`input__fields_property contactUsTextarea${errors.message ? ' field-error' : ''}`}
                    maxLength={1000}
                    rows={4}
                />
                {errors.message && <p className="error_div">{errors.message}</p>}
            </div>

            {/* List your property checkbox (only when coming from 'List your property') */}
            {showListPropertyCheckbox && (
                <div className="input_field_inner contactUsCheckboxWrapper">
                    <label className="contactUsCheckboxLabel" htmlFor="cu_list_property">
                        <input
                            id="cu_list_property"
                            type="checkbox"
                            checked={listPropertyChecked}
                            onChange={(e) => {
                                setListPropertyChecked(e.target.checked);
                                if (errors.list_property) {
                                    setErrors((prev) => ({ ...prev, list_property: '' }));
                                }
                            }}
                            className={`contactUsCheckboxInput ${errors.list_property ? 'checkbox-error' : ''}`}
                        />
                        <span>
                            I want to list my property. I agree to receive communications from thestaying.com regarding property listings and related services, and I accept the{' '}
                            <a
                                href={`/${localStorage.getItem('i18nextLng') || 'en'}/terms-conditions`}
                                onClick={handleTermsClick}
                                className="contactUsTermsLink"
                            >
                                Terms and Conditions
                            </a> and <button
                                type="button"
                                onClick={handlePartnerAgreementClick}
                                className="contactUsTermsLink partnerAgreementLink"
                            >
                                Partner Agreement
                            </button>
                            .
                        </span>
                    </label>
                    {errors.list_property && <p className="error_div">{errors.list_property}</p>}
                </div>
            )}

            {/* Footer row with Captcha on left and Action buttons on right */}
            <div className="modfooterbtn contactUsFooterRow">
                <div className="captchaSectionLeft">
                    <div className="captchaCanvasBox">
                        <canvas
                            ref={canvasRef}
                            width="98"
                            height="34"
                            className="captchaCanvas"
                        />
                        <button
                            type="button"
                            className="captchaRefreshBtn"
                            onClick={generateCaptcha}
                            title="Refresh Captcha"
                        >
                            <i className="fa fa-refresh" aria-hidden="true"></i>
                        </button>
                    </div>
                    <input
                        type="text"
                        name="captcha"
                        value={enteredCaptcha}
                        onChange={(e) => {
                            setEnteredCaptcha(e.target.value);
                            setCaptchaError(false);
                        }}
                        placeholder="Captcha"
                        maxLength={5}
                        autoComplete="off"
                        className={`captchaInput ${captchaError ? 'captcha-error' : ''}`}
                    />
                </div>

                <div className="contactUsBtnRight">
                    <CustomButton
                        className="savebtn"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? 'Sending...' : 'Send'}
                    </CustomButton>
                    <CustomButton
                        className="cancelbtn"
                        onClick={handleCancel}
                        disabled={loading}
                    >
                        Cancel
                    </CustomButton>
                </div>
            </div>
        </div>
    );



    return (
        <>
            {/* Main Contact Us Modal */}
            <ModalGlobal
                show={show}
                onHide={handleCancel}
                className="modalcustomize secondtypemodal samecontentbox changePasswordModal contactUsModal"
                headerclassName="close_btn_icon"
                title={title}
                headerShow={true}
                closeButton={true}
                body={formBody}
                footer={false}
                backdrop="static"
                keyboard={false}
                minimizeModal={false}
            />

            {/* Partner Agreement Modal */}
            <ModalGlobal
                show={partnerAgreementOpen}
                onHide={handlePartnerAgreementClose}
                title="TheStaying.com - Partner Agreement"
                className="modalcustomize partnerAgreementModal"
                headerclassName="close_btn_icon"
                headerShow={true}
                footer={false}
                closeButton={true}
                backdrop="static"
                keyboard={false}
                minimizeModal={false}
                body={
                    <div className="partnerAgreementContent">
                        {partnerAgreementLoading && !partnerAgreementError && (
                            <div className="partnerAgreementLoader">
                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                >
                                    <span className="sr-only">Loading...</span>
                                </div>
                                <p>Loading Partner Agreement...</p>
                            </div>
                        )}

                        {partnerAgreementError && (
                            <div className="partnerAgreementLoader">
                                <p>Unable to load the Partner Agreement.</p>
                            </div>
                        )}

                        {partnerAgreementOpen && (
                            <iframe
                                title="TheStaying.com - Partner Agreement"
                                src={`${process.env.PUBLIC_URL || ""}/partner-agreement.html`}
                                onLoad={() => {
                                    setPartnerAgreementLoading(false);
                                }}
                                onError={() => {
                                    setPartnerAgreementLoading(false);
                                    setPartnerAgreementError(true);
                                }}
                                style={{
                                    width: "100%",
                                    height: "65vh",
                                    border: "none",
                                    display:
                                        partnerAgreementLoading || partnerAgreementError
                                            ? "none"
                                            : "block"
                                }}
                            />
                        )}
                    </div>
                }
            />
        </>
    );
}

export default ContactUsModal;
